import {
  useCombobox,
  GetItemPropsOptions
} from "downshift";
import * as _ from "lodash";
import debounce from "lodash.debounce";
import * as React from "react";
import styled, { css } from "styled-components";
import { useHotkeys } from "react-hotkeys-hook";

import { InputCss } from "src/library/components/Forms/Input";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import { Text } from "src/library/components/Text";
import Icon from "src/library/components/Icon/Icon";
import { Icons } from "src/library/components/Icon/types";
import {
  IProductCategory,
  useProductsApi
} from "src/core/products/api/products.api";

interface Props {
  onSelect: (category: IProductCategory) => void;
  placeholder: string;
  minimumChars?: number;
}

export interface SearchRowProps extends GetItemPropsOptions<IProductCategory> {
  productCategory: IProductCategory;
  disabled: boolean;
  highlighted: boolean;
}
export const ProductCategorySearchComponent: React.ReactFCWithChildren<Props> = props => {
  const [ searchTerm, setSearchTerm ] = React.useState("");
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [ listSearchResponse, getSearch ] = useProductsApi("LIST_PRODUCT_CATEGORIES");
  const results = listSearchResponse.data ? listSearchResponse.data : [];

  const downshiftProps = useCombobox<IProductCategory>({
    items: results,
    itemToString: () => "",
    onSelectedItemChange: changes => {
      if (changes.selectedItem) {
        props.onSelect(changes.selectedItem);
      }
    }
  });

  const performSearch = debounce((e: React.ChangeEvent<HTMLInputElement>) => {
    const minimumChars = props.minimumChars || 1;

    if (e.target.value.length >= minimumChars) {
      getSearch({
        filters: { name: { $like: `%${e.target.value}%` } },
        branchId: null
      });
      setSearchTerm(e.target.value);
    }
  }, 500);

  /// hot key for control + s to search
  useHotkeys("ctrl+s", () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  });

  return (
    <SearchComponent isOpen={downshiftProps.isOpen}>
      <div
        className="search-wrapper"
      >
        <Icon
          icon={Icons.SEARCH}
          $fillColor="lightGrey"
        />

        <input
          ref= {inputRef}
          {...downshiftProps.getInputProps({
            placeholder: props.placeholder || "Search",
            className: "search-input",
            id: "search",
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
              e.persist();
              performSearch(e);
            }
          }, { suppressRefError: true })}
        />
      </div>

      <div className="search-results-wrapper">
        <ul
          className={`${downshiftProps.isOpen && "active"} search-results`}
          {...downshiftProps.getMenuProps({}, { suppressRefError: true })}
        >
          <HeaderWrapper>
            <Text weight="bold">
              {searchTerm.length ? `Showing results for "${searchTerm}"` : ""}
            </Text>
          </HeaderWrapper>

          {downshiftProps.isOpen && (
            <>
              {results.map((item, index) => (
                <div
                  key={`${item.id}-${index}`}
                  {...downshiftProps.getItemProps({
                    item,
                    index
                  })}
                >
                  <DropdownItem
                    disabled={false}
                    highlighted={index === downshiftProps.highlightedIndex}
                  >
                    <Text className="code">
                      #
                      {item.name}
                    </Text>
                  </DropdownItem>
                </div>
              ))}

              {!results.length && !listSearchResponse.error && !listSearchResponse.pending && (
                <DropdownItem>
                  <Text className="details">
                    Nothing found
                  </Text>
                </DropdownItem>
              )}

              {listSearchResponse.error && !listSearchResponse.pending && (
                <DropdownItem>
                  <Text className="error">
                    An unknown error occurred whilst performing your search, please try again.
                  </Text>
                </DropdownItem>
              )}
            </>
          )}
        </ul>
      </div>

      {listSearchResponse.pending && (
        <LoadingSpinner
          className="loading-spinner"
          type="circular"
          position="inline"
          size={20}
        />
      )}
    </SearchComponent>
  );
};

const SearchComponent = styled.div<{ isOpen: boolean }>`
  ${({ theme, isOpen }) => css`
    display: flex;
    flex: 1;
    height: 40px;
    width: 100%;

    width: 300px;
    position: relative;
    z-index: 1000;

    [role="combobox"] {
      width: 100%;
    }

    .search-wrapper {
      position: relative;

      svg {
        position: absolute;
        left: 5px;
        top: 50%;
        transform: translateY(-50%);
      }

      .search-input {
        ${InputCss};
        width: 240px;
        border: solid 2px ${theme.colors.lightestGrey};
        border-radius: ${theme.borderRadius};
        padding-left: 35px;
        transition: ${theme.transition.quickest};
        font-size: ${theme.fonts.sizes.p};

        &:hover,
        &:focus {
          border-color: ${theme.colors.primaryDark};
          background-color: ${theme.colors.white};
        }

        padding-right: 50px;
        width: 100%;
        height: 100%;
        font-size: ${theme.fonts.sizes.p};

        &:focus {
          ::placeholder {
            color: ${theme.colors[ "primaryDark" ]};
          }
        }
      }
      &:hover {
        svg {
          fill: ${theme.colors.lightGrey};
        }
      }
    }

    .loading-spinner {
      position: absolute;
      top: 50%;
      right: 15px;
      transform: translateY(-50%);
    }

    .search-results-wrapper {
      position: absolute;
      z-index: 1000;
      left: 0;
      overflow-y: visible;
      top: 40px;
      padding-top: 40px;
      min-width: ${isOpen ? "300px" : "0px"};
    }

    .search-results {
      max-height: 300px;
      overflow-y: auto;
      width: 100%;
      border: 2px solid ${theme.colors.lightestGrey};
      border-radius: 0px 0px ${theme.borderRadius} ${theme.borderRadius};
      border-top: 0;
      background: ${theme.colors.white};
      display: none;
      list-style: none;

      &.active {
        display: block;
      }
    }
  `};
`;

const HeaderWrapper = styled.div`
  ${({ theme }) => css`
    width: 100%;
    position: absolute;
    top: 0;
    height: 40px;
    background: ${theme.colors.white};
    padding: ${theme.spacing.padding.sm};
  `}
`;

const DropdownItem = styled.li<{ highlighted?: boolean; disabled?: boolean; }>`
  ${({
    theme, highlighted, disabled
  }) => css`
    height: 40px;
    padding: ${theme.spacing.padding.sm};
    display: flex;
    align-items: center;
    cursor: pointer;
    width: 100%;
    background: ${disabled ? theme.colors.lightGrey : highlighted ? theme.colors.primaryDark : "white"};
    color: ${disabled || highlighted ? "white" : theme.colors.primaryDark};
    pointer-events: ${disabled ? "none" : "all"};

    .details {
      padding: 20px;
    }

    .error {
      color: ${theme.colors.error};
    }

    .code {
      font-weight: bold;
      padding: 20px;
      padding-right: 0;
    }
  `}
`;
